import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TaskTypeStatus, type TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import EToolTip from 'components/others/EToolTip';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgTrash from 'components/svg/SvgTrash';
import SolarIcon from 'components/icons/SolarIcon';

interface TaskActionsProps {
    task: TaskCardType;
    onTaskUpdated: (task: TaskCardType) => void;
    onRequestResponse: () => void;
    onRequestReject: () => void;
    rejectFormOpen?: boolean;
}

const TaskActions = ({
    task,
    onTaskUpdated,
    onRequestResponse,
    onRequestReject,
    rejectFormOpen = false,
}: TaskActionsProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { showDlg } = useModal();
    const { showToast } = useToast();
    const [busy, setBusy] = useState(false);

    const userId = Number(auth?.user?.ID || 0);
    const status = Number(task.taskStatus);
    const isExecutor = userId > 0 && userId === Number(task.taskExecutor);
    const isController = userId > 0 && userId === Number(task.taskController);
    const isOwner = userId > 0 && userId === Number(task.userId);
    const isDeleted = Boolean(Number(task.taskDeleted));

    const showTakeInWork = !isDeleted && status === TaskTypeStatus.NEW && isExecutor;
    const showComplete = !isDeleted && status === TaskTypeStatus.INWORK && isExecutor;
    const showAccept = !isDeleted && status === TaskTypeStatus.COMPLETED && isController;
    const showReject = showAccept;
    const showCancel =
        !isDeleted &&
        (status === TaskTypeStatus.NEW || status === TaskTypeStatus.INWORK) &&
        (isOwner || isController);
    const showDelete =
        !isDeleted && status === TaskTypeStatus.NEW && (isOwner || isController);

    if (!showTakeInWork && !showComplete && !showAccept && !showCancel && !showDelete) {
        return null;
    }

    const runAction = async (action: () => Promise<apiTask.TaskActionResult | null>) => {
        setBusy(true);
        const result = await action();
        setBusy(false);
        if (!result?.stat || !result.task) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        onTaskUpdated(result.task);
    };

    const confirmDangerAction = (
        confirmText: string,
        action: () => Promise<apiTask.TaskActionResult | null>,
        withTrashIcon = true,
    ) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1] = {
            ...buttons[1],
            handleClick: () => {
                showDlg(false);
                void runAction(action);
            },
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        {withTrashIcon ? (
                            <SvgTrash className="text-danger" />
                        ) : (
                            <SolarIcon name="x-circle" className="fs-4" />
                        )}
                        <span className="ps-2">{confirmText}</span>
                    </h4>
                    {task.taskName ? <h5 className="mb-0">{task.taskName}</h5> : null}
                </>
            ),
        });
    };

    return (
        <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
            {showTakeInWork && (
                <Button
                    size="sm"
                    variant="info"
                    disabled={busy}
                    onClick={() =>
                        void runAction(() =>
                            apiTask.updateTaskStatus(task.ID, TaskTypeStatus.INWORK),
                        )
                    }
                >
                    {spinnerTitle(
                        <>
                            <SolarIcon name="play-fill" className="me-1" />
                            {t('btnTakeInWork')}
                        </>,
                        busy,
                        t('common:buttons.saving'),
                    )}
                </Button>
            )}

            {showComplete && (
                <Button size="sm" variant="success" disabled={busy} onClick={onRequestResponse}>
                    <SolarIcon name="check2-all" className="me-1" />
                    {t('btnComplete')}
                </Button>
            )}

            {showAccept && (
                <Button
                    size="sm"
                    variant="success"
                    disabled={busy || rejectFormOpen}
                    onClick={() =>
                        void runAction(() =>
                            apiTask.updateTaskStatus(task.ID, TaskTypeStatus.ACCEPTED),
                        )
                    }
                >
                    {spinnerTitle(
                        <>
                            <SolarIcon name="hand-thumbs-up" className="me-1" />
                            {t('btnAccept')}
                        </>,
                        busy,
                        t('common:buttons.saving'),
                    )}
                </Button>
            )}

            {showReject && (
                <Button
                    size="sm"
                    variant={rejectFormOpen ? 'warning' : 'outline-warning'}
                    disabled={busy}
                    onClick={onRequestReject}
                >
                    <SolarIcon name="arrow-return-left" className="me-1" />
                    {t('btnReturnToWork')}
                </Button>
            )}

            {showCancel && (
                <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={busy || rejectFormOpen}
                    onClick={() =>
                        confirmDangerAction(
                            t('confirmCancel'),
                            () => apiTask.updateTaskStatus(task.ID, TaskTypeStatus.CANCELLED),
                            false,
                        )
                    }
                >
                    {spinnerTitle(
                        <>
                            <SolarIcon name="x-circle" className="me-1" />
                            {t('btnCancel')}
                        </>,
                        busy,
                        t('common:buttons.saving'),
                    )}
                </Button>
            )}

            {showDelete && (
                <EToolTip title={t('btnDelete')}>
                    <Button
                        size="sm"
                        variant="outline-danger"
                        className="px-2"
                        disabled={busy || rejectFormOpen}
                        aria-label={t('btnDelete')}
                        onClick={() =>
                            confirmDangerAction(t('confirmDelete'), () =>
                                apiTask.deleteTask(task.ID),
                            )
                        }
                    >
                        {busy ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                            <SolarIcon name="trash" />
                        )}
                    </Button>
                </EToolTip>
            )}
        </div>
    );
};

export default TaskActions;
